import { describe, type BilingualDescription } from "./description";
/**
 * Tem nhiệt CLabel CT321D — phần THUẦN, kiểm thử được mà không cần máy in.
 *
 * Tách khỏi phần giao tiếp thiết bị (WebUSB) có chủ đích: đúng những chỗ dễ sai
 * nhất — thoát ký tự, chọn đường in theo bảng mã, đo khổ tem — đều kiểm được
 * bằng unit test, còn phần cắm dây thì không.
 */

const CRLF = "\r\n";

/**
 * Khổ tem có sẵn của hệ thống cũ (Settings.js).
 * Giữ nguyên khoá để dữ liệu đã di trú (presetKey "50x30") vẫn khớp.
 */
export const LABEL_PRESETS = [
  { key: "70x50", widthMm: 70, heightMm: 50 },
  { key: "60x40", widthMm: 60, heightMm: 40 },
  { key: "50x30", widthMm: 50, heightMm: 30 },
  { key: "80x50", widthMm: 80, heightMm: 50 },
  { key: "50x70", widthMm: 50, heightMm: 70 },
  { key: "40x30", widthMm: 40, heightMm: 30 },
] as const;

export const CUSTOM_PRESET = "custom";

/** Khổ mặc định khi Cài đặt chưa hề có khổ tem — đúng mặc định của hệ cũ. */
export const DEFAULT_PRESET = LABEL_PRESETS[0];

/** Giới hạn khổ tem hợp lệ (mm). CT321D in khổ 20–100mm. */
export const LABEL_MIN_MM = 20;
export const LABEL_MAX_MM = 100;

export interface LabelSize {
  presetKey: string;
  widthMm: number;
  heightMm: number;
  /**
   * Xoay nội dung 90° khi in (20/09/2026).
   *
   * KHÔNG đổi khổ tem — tem vẫn là `widthMm × heightMm`. Cờ này chỉ đổi cách
   * trang in được gửi cho máy: trang sinh ra ở chiều ngược lại và nội dung
   * xoay vào trong.
   *
   * Vì sao cần: máy in tem nhiệt và driver của chúng không thống nhất chiều
   * nào là "rộng". Cùng một tem 50 × 30 mm, có driver khai 50 × 30 (rộng theo
   * đầu in), có driver khai 30 × 50 (rộng theo chiều giấy chạy). Khai lệch thì
   * bản in ra nằm ngang và bị cắt. Ta không đoán được driver của khách theo
   * quy ước nào, và cũng không sửa được nó từ trình duyệt — nên đưa ra một ô
   * tick để người dùng lật cho khớp, kèm bản xem trước đúng thứ sẽ in ra.
   */
  rotate?: boolean;
}

/**
 * Chuẩn hoá khổ tem đọc từ Cài đặt.
 *
 * Trả về `invalid` thay vì âm thầm ép về 70×50: người dùng gõ nhầm "700" mà hệ
 * thống lặng lẽ đổi thành 70 thì họ tưởng đã lưu đúng, rồi in ra cả loạt tem
 * sai khổ. Giao diện phải NÓI RA.
 */
export function normalizeLabelSize(input: {
  presetKey?: string | null;
  widthMm?: unknown;
  heightMm?: unknown;
  rotate?: unknown;
}): { size: LabelSize } | { invalid: "widthMm" | "heightMm" } {
  const rotate = input.rotate === true;
  const preset = LABEL_PRESETS.find((p) => p.key === input.presetKey);
  if (preset) {
    return { size: { presetKey: preset.key, widthMm: preset.widthMm, heightMm: preset.heightMm, rotate } };
  }

  // Khoá preset LẠ (dữ liệu production đang lưu "CT321D" — tên model, không phải
  // khổ tem) thì rơi về khổ tự nhập, KHÔNG từ chối: kích thước đã lưu vẫn dùng
  // được, chặn ở đây là khoá luôn việc in vì một cái nhãn sai tên.
  //
  // CHƯA CẤU HÌNH GÌ CẢ thì lùi về khổ mặc định của hệ cũ (70×50) và vẫn in
  // được. Phân biệt rõ hai chuyện: "chưa ai vào Cài đặt đặt khổ tem" là trạng
  // thái bình thường của một hệ mới dựng — chặn in vì lý do đó là khoá cả tính
  // năng cho tới khi có người phát hiện ra phải vào Cài đặt, mà thông báo lại
  // không nói điều đó. Còn "đã nhập nhưng nhập sai" thì phải NÓI RA, vì không
  // đoán được người dùng định nhập gì và âm thầm ép về 70/50 sẽ ra cả loạt tem
  // sai khổ mà họ tin là đúng.
  const unset = (v: unknown) => v == null || v === "";
  if (unset(input.widthMm) && unset(input.heightMm)) {
    return { size: { presetKey: DEFAULT_PRESET.key, widthMm: DEFAULT_PRESET.widthMm, heightMm: DEFAULT_PRESET.heightMm, rotate } };
  }

  const w = Number(input.widthMm);
  const h = Number(input.heightMm);
  if (!Number.isFinite(w) || w < LABEL_MIN_MM || w > LABEL_MAX_MM) return { invalid: "widthMm" };
  if (!Number.isFinite(h) || h < LABEL_MIN_MM || h > LABEL_MAX_MM) return { invalid: "heightMm" };
  return { size: { presetKey: CUSTOM_PRESET, widthMm: w, heightMm: h, rotate } };
}

/**
 * Thoát dữ liệu trước khi ghép vào luồng lệnh TSPL.
 *
 * Lệnh TSPL phân tách bằng CRLF, nên một mô tả sản phẩm có ký tự xuống dòng sẽ
 * TỰ TÁCH THÀNH LỆNH MỚI — dữ liệu người dùng nhập trở thành lệnh máy in. Hàm
 * escapeTspl của hệ thống cũ chỉ đổi dấu nháy kép và gạch chéo ngược, bỏ sót
 * đúng chỗ này.
 *
 * Cắt độ dài SAU khi thoát, để một chuỗi dài không đẩy dấu nháy đóng ra ngoài
 * giới hạn và làm hỏng cả lệnh.
 */
export function escapeTspl(value: unknown, maxLen = 64): string {
  return String(value ?? "")
    // Mọi ký tự điều khiển (gồm CR/LF/TAB) thành khoảng trắng: đây chính là
    // chỗ hệ thống cũ bỏ sót, và là chỗ dữ liệu biến thành lệnh máy in.
    // eslint-disable-next-line no-control-regex -- CỐ Ý: lọc bỏ ký tự điều khiển trước khi gửi ra máy in tem / chứng từ. Đó chính là việc của dòng này.
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/"/g, "'")
    .replace(/\\/g, "/")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLen);
}

/**
 * Nội dung ngoài ASCII thì KHÔNG dùng font dựng sẵn của máy in.
 *
 * Font trong máy chỉ có ASCII: mô tả tiếng Việt có dấu hoặc tiếng Trung in ra
 * là ký tự rác. Hệ thống cũ không kiểm nên tem tiếng Việt luôn sai — mà hệ mới
 * chạy ba thứ tiếng. Ngoài ASCII thì phải vẽ ảnh rồi gửi bằng lệnh BITMAP, hoặc
 * đi đường in qua hộp thoại hệ điều hành.
 */
export function needsBitmap(lines: readonly string[]): boolean {
  return lines.some((l) => /[^\x20-\x7e]/.test(l));
}

/**
 * Năm trường của tem, ĐÚNG thứ tự hệ thống cũ in ra.
 *
 * Giữ nguyên thứ tự và khoảng cách: nhân viên đã quen đọc tem này trên bàn làm
 * việc, đổi bố cục là bắt họ học lại một thứ không cần đổi.
 */
export interface LabelContent {
  /** (1) Mã sản phẩm — in TRẦN, không có nhãn, font to nhất. */
  productRef: string;
  /** (2) Mô tả — bọc tối đa 2 dòng. */
  description?: string | null;
  /** (3) Màu — 1 dòng. */
  color?: string | null;
  /** (4) Cỡ — bọc tối đa 2 dòng. */
  size?: string | null;
  /** (5) Số lượng mẫu — 1 dòng; người dùng ghi đè được lúc in. */
  sampleQty?: string | null;
  /**
   * Số lô — bổ sung của hệ MỚI, đặt SAU 5 trường cũ và chỉ in khi còn chỗ
   * trước chân tem. Đây đúng là chỗ truy xuất nguồn gốc lô cần đến, nhưng nó
   * không được đẩy trường nào của hệ cũ ra khỏi tem.
   */
  lotNumber?: string | null;
  /** Mã vạch Code128 in ở chân tem; bỏ trống thì không in. */
  barcode?: string | null;
  count: number;
  /**
   * Chân tem: tên công ty và ngày in ('Hisense Button  DD/MM/YYYY').
   *
   * Hệ cũ in dòng này. Nó không trang trí: tem dán lên túi mẫu nằm trong kho
   * hàng tháng trời, và ngày in là thứ duy nhất trên tem cho biết nó được dán
   * ở đợt nào — hai mẫu cùng mã, khác lần gửi, chỉ phân biệt được nhờ nó.
   *
   * Người gọi truyền vào thay vì hàm tự lấy `new Date()`: bộ dựng lệnh TSPL
   * phải THUẦN để kiểm thử được, một hàm tự đọc đồng hồ thì không.
   */
  footer?: string | null;
  /**
   * Ngôn ngữ in trên tem (Roary 12/10/2026): chọn LÚC IN, không phải thuộc tính của sản phẩm — cùng một kho sản phẩm dùng chung, đội
   * Việt Nam cần tem thuần tiếng Anh, đội Trung Quốc cần tiếng Hoa hoặc cả hai. `zh` đổi cả tiêu đề các dòng sang chữ Hoa. Vắng = `en`.
   */
  language?: LabelLanguage;
  /** Mô tả tiếng Hoa — chỉ có khi in "cả hai": in thành dòng riêng ngay dưới mô tả tiếng Anh (mỗi bản một dòng, tổng vẫn 2 dòng). */
  descriptionZh?: string | null;
}

export const LABEL_LANGUAGES = ["en", "zh", "both"] as const;
export type LabelLanguage = (typeof LABEL_LANGUAGES)[number];

export const isLabelLanguage = (v: unknown): v is LabelLanguage => (LABEL_LANGUAGES as readonly unknown[]).includes(v);

/** Nguồn chữ hai thứ tiếng của một tem: mô tả và màu, mỗi thứ một bản Anh và một bản Hoa (có thể trống). */
export interface BilingualLabelSource extends BilingualDescription {
  color?: string | null;
  colorZh?: string | null;
}

const cleanText = (v: string | null | undefined) => (v ?? "").trim();

/**
 * Chữ in trên tem theo ngôn ngữ chọn lúc in.
 *
 * - `en`: mô tả / màu tiếng Anh; ô tiếng Anh trống thì lùi về bản tiếng Hoa (như `describe`) — tem không bao giờ trống vì thiếu bản dịch.
 * - `zh`: ngược lại.
 * - `both`: mô tả tiếng Anh + mô tả tiếng Hoa thành hai dòng (bản Hoa trùng bản Anh hoặc trống thì chỉ một dòng); màu ghép "Anh Hoa".
 */
export function labelTexts(source: BilingualLabelSource, language: LabelLanguage): Pick<LabelContent, "description" | "descriptionZh" | "color" | "language"> {
  const en = cleanText(source.color), zh = cleanText(source.colorZh);
  if (language === "en") return { language, description: describe(source, "en") || null, color: en || zh || null };
  if (language === "zh") return { language, description: describe(source, "zh") || null, color: zh || en || null };
  const descEn = cleanText(source.description), descZh = cleanText(source.descriptionZh);
  return {
    language,
    description: descEn || descZh || null,
    descriptionZh: descEn && descZh && descZh !== descEn ? descZh : null,
    color: [en, zh].filter((v, i, all) => v && all.indexOf(v) === i).join(" ") || null,
  };
}

/** CT321D in ở 203 dpi. Dùng đúng hệ số của hệ cũ, không làm tròn thành 8. */
export const DOTS_PER_MM = 203 / 25.4;
const mm = (v: number) => Math.round(v * DOTS_PER_MM);

/** Bề rộng ước lượng một ký tự (dot) của font dựng sẵn, theo hệ số nhân. */
const CHAR_W = { "1": 8, "2": 14 } as const;

/** Số tem hợp lệ cho một lần in. */
export function clampLabelCount(n: unknown): number {
  const v = Math.floor(Number(n));
  if (!Number.isFinite(v) || v < 1) return 1;
  return Math.min(99, v);
}

/**
 * Cắt một chuỗi cho vừa bề ngang tem, nối '…' khi phải cắt.
 *
 * Cắt theo SỐ KÝ TỰ ước lượng từ bề rộng font dựng sẵn — máy in không báo lại
 * bề rộng thực, nên đây là cách duy nhất không cần đo bằng canvas.
 */
export function fitChars(text: string, usableDots: number, font: "1" | "2", mul: number): string {
  const max = Math.max(8, Math.floor(usableDots / (CHAR_W[font] * mul)));
  return text.length <= max ? text : text.slice(0, max - 1) + "…";
}

/** Bọc chuỗi thành tối đa `maxLines` dòng vừa bề ngang. */
export function wrapChars(
  text: string,
  usableDots: number,
  font: "1" | "2",
  mul: number,
  maxLines: number,
): string[] {
  const per = Math.max(8, Math.floor(usableDots / (CHAR_W[font] * mul)));
  const words = text.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= per) {
      cur = next;
      continue;
    }
    if (cur) out.push(cur);
    cur = w.length > per ? w.slice(0, per - 1) + "…" : w;
    if (out.length === maxLines) break;
  }
  if (cur && out.length < maxLines) out.push(cur);
  return out.slice(0, maxLines);
}

/** Các trường có thể in ra tem, đúng thứ tự in. */
export const LABEL_FIELDS = ["productRef", "description", "color", "size", "sampleQty", "lotNumber"] as const;
export type LabelField = (typeof LABEL_FIELDS)[number];

export interface LabelPlan {
  payload: string;
  /**
   * Trường CÓ dữ liệu nhưng KHÔNG in được vì hết chỗ trên tem.
   *
   * Hết chỗ là chuyện vật lý của tờ tem, không tránh được. Nhưng người bấm in
   * phải biết mình vừa in ra một cái tem thiếu LOT — không thì cả lô hàng mất
   * dấu truy xuất mà màn hình vẫn báo in xong.
   */
  omitted: LabelField[];
}

/**
 * Dựng luồng lệnh TSPL cho nhãn CHỈ CÓ ASCII, theo đúng đặc tả hệ thống cũ,
 * kèm danh sách trường phải bỏ vì hết chỗ.
 *
 * Nội dung ngoài ASCII phải đi đường bitmap hoặc hộp thoại hệ điều hành (xem
 * needsBitmap) — hàm này ném lỗi thay vì im lặng in ra ký tự rác.
 */
export function buildTsplPlan(size: LabelSize, content: LabelContent): LabelPlan {
  const fields = [
    content.productRef,
    content.description,
    content.color,
    content.size,
    content.sampleQty,
    content.lotNumber,
  ].map((v) => escapeTspl(v));
  if (needsBitmap(fields)) throw new Error("non_ascii_requires_bitmap");

  const widthDots = mm(size.widthMm);
  const heightDots = mm(size.heightMm);
  const leftX = mm(6);
  const usable = widthDots - leftX * 2;
  // Chân tem CHỈ giữ chỗ khi thật sự in mã vạch. Giữ chỗ vô điều kiện ăn mất
  // 12mm trên tem 30mm — tức 40% chiều cao — và đẩy hai trường cuối của hệ cũ
  // ra khỏi tem trong khi chẳng có gì được in ở đó.
  const barcodeText = content.barcode ? escapeTspl(content.barcode, 40) : "";
  const footerText = escapeTspl(content.footer, 40);
  // Dải đáy được GIỮ CHỖ đúng bằng những gì thật sự in ở đó. Cộng cả chân tem
  // vào đây chứ không chỉ mã vạch: thiếu bước này thì dòng nội dung cuối được
  // phép chạy tới sát đáy và đè lên chính dòng chân tem.
  const footerLineY = heightDots - mm(3.2);
  const footerY = heightDots - (barcodeText ? mm(11) : mm(2)) - (footerText ? mm(3.2) : 0);

  const out: string[] = [
    `SIZE ${size.widthMm} mm,${size.heightMm} mm`,
    "GAP 2 mm,0 mm",
    "DIRECTION 0",
    "REFERENCE 0,0",
    "CLS",
  ];
  let y = mm(4);
  const omitted: LabelField[] = [];
  /** Trả về false khi dòng KHÔNG in được — người gọi ghi nhận trường bị mất. */
  const line = (text: string, font: "1" | "2", xMul: number, yMul: number, step: number) => {
    if (!text) return true;
    if (y + step > footerY) return false;
    out.push(`TEXT ${leftX},${y},"${font}",0,${xMul},${yMul},"${text}"`);
    y += step;
    return true;
  };

  // (1) Mã sản phẩm — in TRẦN, không nhãn, font 2 nhân đôi bề ngang.
  if (!line(fitChars(escapeTspl(content.productRef), usable, "2", 2), "2", 2, 1, mm(5.5))) omitted.push("productRef");

  const small = (field: LabelField, label: string, value: string | null | undefined, maxLines: number) => {
    const v = escapeTspl(value);
    if (!v) return; // Giá trị rỗng thì BỎ HẲN dòng, không in "Desc:" trống.
    let lost = false;
    for (const l of wrapChars(`${label}: ${v}`, usable, "1", 1, maxLines)) {
      if (!line(l, "1", 1, 1, mm(3.2))) lost = true;
    }
    // Mất DÙ CHỈ một dòng cũng là mất chữ mà trên tem không có dấu hiệu gì.
    if (lost) omitted.push(field);
  };
  small("description", "Desc", content.description, 2);
  small("color", "Color", content.color, 1);
  small("size", "Size", content.size, 2);
  small("sampleQty", "Sample Qty", content.sampleQty, 1);
  // Bổ sung của hệ mới, đặt SAU 5 trường cũ — `line` đã tự dừng khi hết chỗ.
  small("lotNumber", "LOT", content.lotNumber, 1);

  if (barcodeText) {
    out.push(`BARCODE ${leftX},${footerY},"128",${mm(8)},1,0,2,2,"${barcodeText}"`);
  }
  // Chân tem in SAU mã vạch, ở dải đáy đã giữ chỗ sẵn phía trên.
  if (footerText) {
    out.push(`TEXT ${leftX},${footerLineY},"1",0,1,1,"${fitChars(footerText, usable, "1", 1)}"`);
  }

  out.push(`PRINT ${clampLabelCount(content.count)},1`);
  // CRLF là dấu kết thúc lệnh của TSPL; thiếu dòng cuối thì lệnh PRINT có thể
  // không được máy nhận.
  return { payload: out.join(CRLF) + CRLF, omitted };
}

/** Chuỗi lệnh TSPL. Dùng buildTsplPlan khi cần biết trường nào không in được. */
export function buildTspl(size: LabelSize, content: LabelContent): string {
  return buildTsplPlan(size, content).payload;
}

/** Lệnh tự kiểm tra của máy in — in một tem mẫu để xác nhận đã kết nối. */
export const TSPL_SELFTEST = `SELFTEST${CRLF}`;
